import axios from 'axios'
import env from 'config'
import { setNotification, useNotifcation } from 'context/notification'
import { useOtpAction } from 'context/otpAction'
import { ReqMethods, ResponseStatus } from 'enums'
import { useAuth, useLogout } from './auth'

const BASE_URL = `${env.API_URL}/v1`

// Paths that should NEVER trigger the OTP gate. Anything in /auth/* would
// loop infinitely (the OTP send/verify itself uses POST), and we also let
// raw file/image upload helpers through so they aren't blocked. Token
// verification is also exempt — it's a passive read-style call.
const OTP_EXEMPT_PATHS = [
  '/auth/login',
  '/auth/login/verifyOtp',
  '/auth/admin/sendActionOtp',
  '/auth/admin/verifyActionOtp',
  '/auth/signin',
  '/auth/signup',
  '/auth/forgotPassword',
  '/auth/resetPassword',
  '/auth/verifyOtp',
  '/auth/verifytoken',
  '/files/upload',
  '/utils/upload',
  '/media-handler/upload',
]

// Methods that always go through the OTP gate — every PUT/DELETE is
// considered "mutating" and gets gated. POSTs are gated only when the
// path matches a sensitive prefix (bookings actions, settings updates,
// admin user actions). GET is always free.
const OTP_SENSITIVE_POST_PATTERNS = [
  /\/bookings(\/|$)/i,
  /\/super_admin_settings(\/|$)/i,
  /\/auth\/loginuser$/i,
  /\/auth\/user-update$/i,
  /\/auth\/deleteUser$/i,
  /\/geo-allowlist(\/|$)/i,
]

const requestNeedsOtp = (method, path) => {
  const m = String(method || '').toLowerCase()
  if (m === 'get') return false
  if (OTP_EXEMPT_PATHS.some((p) => path.endsWith(p))) return false
  if (m === 'put' || m === 'delete') return true
  if (m === 'post') {
    return OTP_SENSITIVE_POST_PATTERNS.some((re) => re.test(path))
  }
  return false
}

function useServiceHandler() {
  const [, dispatch] = useNotifcation()
  const Logout = useLogout()

  return (fn) =>
    async (method, path, { query, payload, token, apiKey, toaster, message, error } = {}) => {
      try {
        const res = await fn(method, path, { query, payload, token, apiKey })
        console.log('API - RESPONSE', res, toaster, message, error)
        toaster && setNotification(dispatch, { open: true, message: message || res.data.message, title: 'Success' })

        return { response: res.data }
      } catch (err) {
        console.log('API- ERROR', err.response?.data || err)

        // expire error : jwt expired
        if (err.response && err.response.status === ResponseStatus.UNAUTHORIZED) {
          setNotification(dispatch, { open: true, message: error || 'session expired', title: 'Error', severity: "error" })
          setTimeout(Logout, 4000)
          return { error: err.response?.data || err }
        }

        const customError = err.response?.data?.error ? `${err.response?.data?.message} \n${err.response?.data?.error}` : err.response?.data?.message

        toaster && setNotification(dispatch, { open: true, message: error || customError || err.message, severity: "error", title: 'Error' })
        return { error: err.response ? err.response.data : err }
      }
    }
}

function useCallService() {
  const authToken = useAuth()
  const serviceHandler = useServiceHandler()
  const { requireOtp } = useOtpAction()

  const CallService = async (method, path, { query, payload, token = true, apiKey = null, skipOtp = false } = {}) => {
    const pathname = query ? `${path}?${query}` : path;

    // OTP gate: any mutating call that isn't on the exempt list is held
    // until the admin verifies their identity (or the 3-hour session
    // cache is still valid, in which case it's a no-op pass-through).
    // `skipOtp` is an opt-out for the rare internal call that needs to
    // bypass (we don't expose this to feature code).
    if (!skipOtp && authToken && requestNeedsOtp(method, path)) {
      const ok = await requireOtp()
      if (!ok) {
        // User cancelled the OTP modal. Synthesise an axios-like error so
        // the upstream handler renders a clean message and the calling
        // page can do `if (error) return`.
        const err = new Error('Action cancelled — OTP not verified.')
        err.response = {
          status: 401,
          data: { success: false, message: 'Action cancelled — OTP not verified.' },
        }
        throw err
      }
    }

    const headers = {};
    if (token) headers['x-auth-token'] = `Bearer ${authToken || token}`;
    if (apiKey) headers.apiKey = apiKey;

    return axios({
      method,
      url: pathname,
      data: payload || {},   // ✅ always map body → data
      headers,
    });
  };


  return serviceHandler(CallService)
}

function useAppServices() {
  const { GET, POST, PUT, DELETE } = ReqMethods
  const CallService = useCallService()

  /**
    Option for service is the object that could has the following properties
    query, payload, token, apiKey
  */

  const APIs = {
    auth: {
      login: (options) => CallService(POST, `${BASE_URL}/auth/login`, options),
      loginVerifyOtp: (options) => CallService(POST, `${BASE_URL}/auth/login/verifyOtp`, options),
      sendActionOtp: (options) => CallService(POST, `${BASE_URL}/auth/admin/sendActionOtp`, options),
      verifyActionOtp: (options) => CallService(POST, `${BASE_URL}/auth/admin/verifyActionOtp`, options),
      get: (options) => CallService(GET, `${BASE_URL}/auth/getallUsers`, options),
      getallUsers: (options) => CallService(GET, `${BASE_URL}/auth/getallUsers`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/auth/deleteUser`, options),
      loginuser: (options) => CallService(POST, `${BASE_URL}/auth/loginuser`, options),

    },
    booking: {
      get: (options) =>
        CallService(GET, `${BASE_URL}/bookings`, options),
      manualPayment: (options) =>
        CallService(POST, `${BASE_URL}/bookings/manualPayment`, options),

      create: (options) =>
        CallService(POST, `${BASE_URL}/bookings`, options),
      update: (options) =>
        CallService(PUT, `${BASE_URL}/bookings`, options),

      runBookingWorkflowByBookingId: (options) =>
        CallService(POST, `${BASE_URL}/bookings/runBookingWorkflowByBookingId`, options),
      applyDiscount: (options) =>
        CallService(POST, `${BASE_URL}/bookings/applyDiscount`, options),
      ledger: (options) =>
        CallService(GET, `${BASE_URL}/bookings/ledger`, options),
    },
    user: {
      get: (options) => CallService(GET, `${BASE_URL}/user`, options),
    },
    content: {
      get: (options) => CallService(GET, `${BASE_URL}/content`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/content`, options),

    },
    blogs: {
      generate_blog: (options) => CallService(POST, `${BASE_URL}/blog/generate_blog`, options),
      create: (options) => CallService(POST, `${BASE_URL}/blog`, options),
      get: (options) => CallService(GET, `${BASE_URL}/blog`, options),
      update_status: (options) => CallService(PUT, `${BASE_URL}/blog/update_status`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/blog`, options),
    },
    linkinbio: {
      posts: (options) => CallService(GET, `${BASE_URL}/linkinbio/posts`, options),
      toggle: (options) => CallService(POST, `${BASE_URL}/linkinbio/toggle`, options),
      update: (options) => CallService(POST, `${BASE_URL}/linkinbio/update`, options),
    },

    bookingChangeRequest: {
      // POST /booking-change-request/
      create: (options) => CallService(POST, `${BASE_URL}/changebooking`, options),

      // GET /booking-change-request/
      get: (options) => CallService(GET, `${BASE_URL}/changebooking`, options),

      // GET /booking-change-request/by-booking?bookingNo=XXX  ✅ fetches the actual request data
      getByBooking: (options) => CallService(GET, `${BASE_URL}/changebooking/by-booking`, options),

      // PUT /booking-change-request/accept   body: { id }
      accept: (options) => CallService(PUT, `${BASE_URL}/changebooking/accept`, options),

      // PUT /booking-change-request/reject   body: { id, rejectionReason }
      reject: (options) => CallService(PUT, `${BASE_URL}/changebooking/reject`, options),

      // DELETE /booking-change-request/   body: { id }
      delete: (options) => CallService(DELETE, `${BASE_URL}/changebooking/`, options),
    },
    files: {
      upload: (options) => CallService(POST, `${BASE_URL}/files/upload`, options),
      get: (options) => CallService(GET, `${BASE_URL}/files`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/files/delete`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/files/update`, options),
    },
    aicollection: {
      generate_blog: (options) => CallService(POST, `${BASE_URL}/aicollection/generate_collection`, options),
      create: (options) => CallService(POST, `${BASE_URL}/aicollection`, options),
      get: (options) => CallService(GET, `${BASE_URL}/aicollection`, options),
      update_status: (options) => CallService(PUT, `${BASE_URL}/aicollection/update_status`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/aicollection`, options),
    },
    aicollection: {
      generate_blog: (options) => CallService(POST, `${BASE_URL}/aicollection/generate_collection`, options),
      create: (options) => CallService(POST, `${BASE_URL}/aicollection`, options),
      get: (options) => CallService(GET, `${BASE_URL}/aicollection`, options),
      update_status: (options) => CallService(PUT, `${BASE_URL}/aicollection/update_status`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/aicollection`, options),
    },


    banner: {
      get: (options) => CallService(GET, `${BASE_URL}/banner`, options),
      create: (options) => CallService(POST, `${BASE_URL}/banner`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/banner/update`, options),
      reorder: (options) => CallService(PUT, `${BASE_URL}/banner/reorder`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/banner`, options),
    },
    notifications: {
      get: (options) => CallService(GET, `${BASE_URL}/notifications`, options),
      single: (options) => CallService(GET, `${BASE_URL}/notifications/single`, options),
      options: (options) => CallService(GET, `${BASE_URL}/notifications/options`, options),
      create: (options) => CallService(POST, `${BASE_URL}/notifications`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/notifications/update`, options),
      toggle: (options) => CallService(PUT, `${BASE_URL}/notifications/toggle`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/notifications`, options),
    },
    chatbotConfig: {
      get: (options) => CallService(GET, `${BASE_URL}/chatbot/config/admin`, options),
      options: (options) => CallService(GET, `${BASE_URL}/chatbot/config/options`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/chatbot/config`, options),
    },
    utils: {
      upload_image: (options) => CallService(POST, `${BASE_URL}/utils/upload/image`, options),
    },


    property_type: {
      create: (options) => CallService(POST, `${BASE_URL}/property_type`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/property_type/filter`, options),
      get: (options) => CallService(GET, `${BASE_URL}/property_type/get`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/property_type`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/property_type`, options),
      uploadCsv: (options) => CallService(POST, `${BASE_URL}/property_type/uploadCsv`, options),
    },
    spacetypes: {
      create: (options) => CallService(POST, `${BASE_URL}/spacetypes`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/spacetypes/filter`, options),
      get: (options) => CallService(GET, `${BASE_URL}/spacetypes/get`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/spacetypes`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/spacetypes`, options),
      uploadCsv: (options) => CallService(POST, `${BASE_URL}/spacetypes/uploadCsv`, options),
    },
    listing: {
      get: (options) => CallService(GET, `${BASE_URL}/listings/get`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/listings`, options),
      toggleHome: (options) => CallService(PUT, `${BASE_URL}/listings/toggle-home`, options),
      toggleChristmasStyled: (options) => CallService(PUT, `${BASE_URL}/listings/toggle-christmas-styled`, options),
      togglePrivate: (options) => CallService(POST, `${BASE_URL}/listings/togglePrivate`, options),
      getReport: (options) => CallService(GET, `${BASE_URL}/listings/getReport`, options),
      delete: (options) =>
        CallService(DELETE, `${BASE_URL}/listings`, options),
      getReport: (options) => CallService(GET, `${BASE_URL}/listings/getReport`, options),
      togglePrivate: (options) => CallService(POST, `${BASE_URL}/listings/togglePrivate`, options),

    },

    property_style: {
      create: (options) => CallService(POST, `${BASE_URL}/property_style`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/property_style/filter`, options),
      get: (options) => CallService(GET, `${BASE_URL}/property_style/get`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/property_style`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/property_style`, options),
      uploadCsv: (options) => CallService(POST, `${BASE_URL}/property_style/uploadCsv`, options),
    },

    Priceperhour: {
      create: (options) => CallService(POST, `${BASE_URL}/priceperhour`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/priceperhour/filter`, options),
      get: (options) => CallService(GET, `${BASE_URL}/priceperhour/get`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/priceperhour`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/priceperhour`, options),
      uploadCsv: (options) => CallService(POST, `${BASE_URL}/priceperhour/uploadCsv`, options),
    },
    superadmin: {
      getProducts: (options) =>
        CallService(GET, `${BASE_URL}/snapshot/superadmin_settings/workflows`, options),
    },
    super_admin_settings: {
      get: (options) => CallService(GET, `${BASE_URL}/super_admin_settings`, options),
      list_workflows: (options) => CallService(GET, `${BASE_URL}/super_admin_settings/list_workflows`, options),
      list_custom_fields: (options) => CallService(GET, `${BASE_URL}/super_admin_settings/list_custom_fields`, options),

      weekly_custom_values: (options) => CallService(GET, `${BASE_URL}/super_admin_settings/weekly_custom_values`, options),
      update_weekly_custom_values: (options) => CallService(POST, `${BASE_URL}/super_admin_settings/update_weekly_custom_values`, options),

      update_workflows: (options) => CallService(POST, `${BASE_URL}/super_admin_settings/update_workflows`, options),

      update_custom_fields: (options) => CallService(POST, `${BASE_URL}/super_admin_settings/update_custom_fields`, options),



      update: (options) => CallService(PUT, `${BASE_URL}/super_admin_settings`, options),
      create: (options) => CallService(POST, `${BASE_URL}/super_admin_settings`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/super_admin_settings`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/super_admin_settings/filter`, options),
    },
    statistics: {
      get: (options) => CallService(GET, `${BASE_URL}/statistics`, options),
      // Monthly Recap. query: month=YYYY-MM | from=YYYY-MM-DD&to=YYYY-MM-DD
      recap: (options) => CallService(GET, `${BASE_URL}/statistics/recap`, options),
    },
    Features: {
      create: (options) => CallService(POST, `${BASE_URL}/features`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/features/filter`, options),
      get: (options) => CallService(GET, `${BASE_URL}/features/get`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/features`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/features`, options),
      uploadCsv: (options) => CallService(POST, `${BASE_URL}/features/uploadCsv`, options),
    },
    brand: {
      get: (options) => CallService(GET, `${BASE_URL}/brand`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/brand`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/brand/filter`, options),
    },
    agency: {
      get: (options) => CallService(GET, `${BASE_URL}/agency`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/agency`, options),
    },
    services: {
      ghl: {
        call_service: (options) => CallService(POST, `${BASE_URL}/services/ghl/`, options),
        oauth_exchange: (options) => CallService(POST, `${BASE_URL}/services/ghl/oauth-exchange`, options),
      },
    },
    boost: {
      adminList: (options) => CallService(GET, `${BASE_URL}/boost/admin/list`, options),
    },
    creativeDirectory: {
      listAdmin: (options) => CallService(GET, `${BASE_URL}/creative-directory/admin`, options),
      getByIdAdmin: (options) =>
        CallService(GET, `${BASE_URL}/creative-directory/admin/${options.params.id}`, options),
      approve: (options) => CallService(POST, `${BASE_URL}/creative-directory/approve`, options),
      reject: (options) => CallService(POST, `${BASE_URL}/creative-directory/reject`, options),
      adminPause: (options) => CallService(POST, `${BASE_URL}/creative-directory/admin/pause`, options),
      listAnalyticsAdmin: (options) =>
        CallService(GET, `${BASE_URL}/creative-directory/admin/analytics`, options),
      getAnalyticsByIdAdmin: (options) =>
        CallService(GET, `${BASE_URL}/creative-directory/admin/analytics/${options.params.id}`, options),
    },
    directoryOptions: {
      listAdmin: (options) => CallService(GET, `${BASE_URL}/directory-options/admin`, options),
      otherSuggestions: (options) =>
        CallService(GET, `${BASE_URL}/directory-options/admin/other-suggestions`, options),
      create: (options) => CallService(POST, `${BASE_URL}/directory-options`, options),
      update: (options) =>
        CallService(PUT, `${BASE_URL}/directory-options/${options.params.id}`, options),
      remove: (options) =>
        CallService(DELETE, `${BASE_URL}/directory-options/${options.params.id}`, options),
      promote: (options) => CallService(POST, `${BASE_URL}/directory-options/promote`, options),
    },
    geoAllowlist: {
      list: (options) => CallService(GET, `${BASE_URL}/geo-allowlist`, options),
      add: (options) => CallService(POST, `${BASE_URL}/geo-allowlist`, options),
      remove: (options) => CallService(DELETE, `${BASE_URL}/geo-allowlist/${options.params.id}`, options),
    },
    conversationPriority: {
      check: (options) => CallService(POST, `${BASE_URL}/conversation-priority/check`, options),
    },
  }

  return APIs
}
const useUploadImage = () => {
  const AppService = useAppServices()
  return ({ toaster, file, desiredPath }) => {
    const form = new FormData()
    form.append('image', file, file.name)
    return AppService.utils.upload_image({
      toaster,
      payload: form,
      query: `desiredPath=${desiredPath}`,
    })
  }
}
const useUploadImages = () => {
  const AppService = useAppServices()
  return ({ toaster, file, desiredPath }) => {
    const form = new FormData()
    form.append('company_logo', file, file.name)
    return AppService.utils.upload_images({
      toaster,
      payload: form,
      query: `desiredPath=${desiredPath}`,
    })
  }
}

const uploadImageToGhl = async (formData) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/media-handler/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return res.data;
  } catch (error) {
    console.error("Upload API error:", error.response?.data || error.message);
    throw error;
  }
};
export { useAppServices, useCallService, useUploadImage, useUploadImages, uploadImageToGhl }

